import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Send, 
  Loader2, 
  CheckCircle,
  Instagram,
  Facebook,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api, { formatApiError } from '../lib/api';
import { CONTACT, SOCIAL } from '../lib/siteConfig';
import { TikTokIcon } from '../components/BrandIcons';
import { validateField, sanitizeString, isSecureInput, errorMessages } from '../lib/validation';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_type: '',
    event_date: '',
    guests: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validationRules = {
    name: { required: true, name: true },
    email: { required: true, email: true },
    phone: { required: true, phone: true },
    event_type: { required: true },
    event_date: { required: true },
    guests: { required: true },
    message: {}
  };

  const validateFieldOnChange = (field, value) => {
    let error = '';
    
    if (!isSecureInput(value)) {
      error = errorMessages.security;
    } else {
      const rules = validationRules[field] || {};
      error = validateField(value, rules);
    }
    
    setFieldErrors(prev => ({ ...prev, [field]: error || '' }));
    return !error;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateFieldOnChange(field, formData[field]);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched[field]) {
      validateFieldOnChange(field, value);
    }
  };

  const validateForm = () => {
    const fields = ['name', 'email', 'phone', 'event_type', 'event_date', 'guests'];
    let isValid = true;
    const newTouched = {};
    
    fields.forEach(field => {
      newTouched[field] = true;
      const valid = validateFieldOnChange(field, formData[field]);
      if (!valid) isValid = false;
    });
    
    setTouched(newTouched);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await api.post('/event-inquiry', {
        name: sanitizeString(formData.name),
        email: sanitizeString(formData.email.toLowerCase()),
        phone: sanitizeString(formData.phone),
        event_type: sanitizeString(formData.event_type),
        event_date: formData.event_date,
        guests: parseInt(formData.guests) || 0,
        message: sanitizeString(formData.message)
      });
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        event_type: '',
        event_date: '',
        guests: '',
        message: ''
      });
      setTouched({});
      setFieldErrors({});
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const renderInput = (field, label, type, placeholder, icon) => {
    const Icon = icon;
    const hasError = touched[field] && fieldErrors[field];
    
    return (
      <div>
        <Label htmlFor={field} className="text-[#5C4033]">{label}</Label>
        <div className="relative mt-2">
          {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#C8962D]" />}
          <Input
            id={field}
            name={field}
            type={type}
            value={formData[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            onBlur={() => handleBlur(field)}
            placeholder={placeholder}
            className={`${Icon ? 'pl-10' : ''} ${hasError ? 'border-red-500 focus:border-red-500' : ''}`}
            data-testid={`contact-${field}-input`}
          />
        </div>
        {hasError && (
          <p className="mt-1 text-sm text-red-600 flex items-center">
            <AlertCircle className="h-4 w-4 mr-1" />
            {fieldErrors[field]}
          </p>
        )}
      </div>
    );
  };

  return (
    <div data-testid="contact-page" className="pt-20 min-h-screen bg-[#FAF7F2]">
      <section className="bg-[#5C4033] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">Contáctanos</h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              Hagamos realidad tu próxima experiencia<br></br><br></br>
              Ya sea para celebrar un momento especial o disfrutar de una estancia en La Finca, estamos aquí para resolver tus dudas y ayudarte a planear cada detalle.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-serif text-2xl text-[#1A1A1A] mb-6">Información de Contacto</h2>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#5C4033]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-6 w-6 text-[#5C4033]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1A1A1A]">Ubicación</h3>
                    <p className="text-[#1A1A1A]/60 text-sm">
                      {CONTACT.addressLine1},<br />
                      {CONTACT.addressLine2}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#5C4033]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-[#5C4033]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1A1A1A]">Teléfono</h3>
                    <a href={CONTACT.phoneHref} className="text-[#5C4033] hover:text-[#C8962D]">
                      {CONTACT.phoneDisplay}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#5C4033]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-[#5C4033]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1A1A1A]">Email</h3>
                    <a href={CONTACT.emailHref} className="text-[#5C4033] hover:text-[#C8962D]">
                      {CONTACT.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#5C4033]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-[#5C4033]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1A1A1A]">Horario de Atención</h3>
                    <p className="text-[#1A1A1A]/60 text-sm">
                      Lunes a viernes <br />
                      8:00am a 8:00pm<br /><br />
                      Sábados <br />
                      8:00am a 8:00pm<br /><br />
                      Domingos<br />
                      8:00am a 7:00pm
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t mt-8">
                <h3 className="font-medium text-[#1A1A1A] mb-4">Síguenos</h3>
                <div className="flex space-x-4">
                  <a
                    href={SOCIAL.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Síguenos en Instagram"
                    className="w-12 h-12 bg-[#5C4033] rounded-xl flex items-center justify-center text-white hover:bg-[#3D5A3E] transition-colors"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                  <a
                    href={SOCIAL.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Síguenos en Facebook"
                    className="w-12 h-12 bg-[#5C4033] rounded-xl flex items-center justify-center text-white hover:bg-[#3D5A3E] transition-colors"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a
                    href={SOCIAL.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Síguenos en TikTok"
                    className="w-12 h-12 bg-[#5C4033] rounded-xl flex items-center justify-center text-white hover:bg-[#3D5A3E] transition-colors"
                  >
                    <TikTokIcon className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-sm"
            >
              <h2 className="font-serif text-2xl text-[#1A1A1A] mb-6">
                Solicita Información Para Tu Evento o Estancia
              </h2>

              {success ? (
                <div className="text-center py-12">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="font-serif text-2xl text-[#1A1A1A] mb-2">¡Mensaje Enviado!</h3>
                  <p className="text-[#1A1A1A]/60 mb-6">Nos pondremos en contacto contigo pronto.</p>
                  <Button
                    onClick={() => setSuccess(false)}
                    variant="outline"
                    className="border-[#5C4033] text-[#5C4033]"
                  >
                    Enviar Otra Consulta
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {renderInput('name', 'Nombre completo *', 'text', 'Tu nombre')}
                    {renderInput('email', 'Correo electrónico *', 'email', 'tu@email.com')}
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    {renderInput('phone', 'Teléfono *', 'tel', '')}
                    <div>
                      <Label htmlFor="event_type" className="text-[#5C4033]">Tipo de evento *</Label>
                      <Select
                        value={formData.event_type}
                        onValueChange={(value) => {
                          handleChange('event_type', value);
                          if (touched.event_type) validateFieldOnChange('event_type', value);
                        }}
                      >
                        <SelectTrigger 
                          className={`mt-2 ${touched.event_type && fieldErrors.event_type ? 'border-red-500' : ''}`}
                          data-testid="contact-event-type-select"
                          onBlur={() => handleBlur('event_type')}
                        >
                          <SelectValue placeholder="Selecciona..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="estancia">Estancia</SelectItem>
                          <SelectItem value="daypass">Day Pass</SelectItem>
                          <SelectItem value="boda">Boda</SelectItem>
                          <SelectItem value="xv">XV Años</SelectItem>
                          <SelectItem value="bautizo">Bautizo</SelectItem>
                          <SelectItem value="cumpleanos">Cumpleaños</SelectItem>
                          <SelectItem value="corporativo">Evento Corporativo</SelectItem>
                          <SelectItem value="otro">Otro</SelectItem>
                        </SelectContent>
                      </Select>
                      {touched.event_type && fieldErrors.event_type && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {fieldErrors.event_type}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    {renderInput('event_date', 'Fecha *', 'date', '')}
                    {renderInput('guests', 'Número de invitados *', 'number', '100')}
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#5C4033]">Mensaje (opcional)</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      onBlur={() => handleBlur('message')}
                      placeholder="Cuéntanos más sobre tu evento..."
                      className={`mt-2 ${touched.message && fieldErrors.message ? 'border-red-500' : ''}`}
                      rows={4}
                      data-testid="contact-message-input"
                    />
                    {touched.message && fieldErrors.message && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {fieldErrors.message}
                      </p>
                    )}
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="w-full bg-[#5C4033] hover:bg-[#3D5A3E] text-white"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Enviar Consulta
                      </>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
